import { action, computed, observable } from "mobx";
import { DelineateWatershedInputStateVM } from "../DelineateWatershedInputStateVM";
import { formatRound } from "cd-eft/utils/mathUtilities";
import MessageCenter from "cd-eft/components/common/MessageCenter/MessageCenterVM";
import { esriProjection, Graphic, GraphicsLayer, MapView, Point, Polyline, Sketch, SpatialReference, esriGeometryEngine, Polygon } from "cd-eft/utils/esriTypes";
import stores from "cd-eft/models/Stores";
import { DesignElementController, HydroWatershedController, StructureController } from "cd-eft/services/eftSetApi";
import { getRainfallCellID } from "cd-eft/services/EFH2/getRainfallCellID";
import { HydroControllerApi } from "cd-eft/services/eftEfh2Api";
import { appConfig } from "cd-eft/config/AppConfig";
import { EftProjectController } from 'cd-eft/services/eftPrjAdminApi'

import { FlowPathSegment, LoadFlowPathSegmentsRespDTO } from "cd-eft/_codegen_/openapi-generator/eft-set";

const efh2Store = stores.AppStore.efh2Store;

export enum SelectedMethod {
    LAG = 0,
    VELOCITY = 1
}

export enum TableMode {
    MANUAL = 0,
    SEGMENT = 1
}

export enum FlowTypeIntegers {
    SHEET = 1,
    SHALLOW = 2,
    CHANNEL = 3
}

export enum ShallowOptionIntegers {
    PAVED = 11,
    UNPAVED = 12
}

export enum SheetOptionIntegers {
    SMOOTH_SURFACE = 1,
    CULTIVATED_LOW = 3,
    CULTIVATED_HIGH = 4,
    GRASS_BERMUDA = 7,
    GRASS_DENSE = 6,
    GRASS_RANGE = 5,
    FALLOW = 2,
    RANGE = 10,
    WOODS_DENSE = 9,
    WOODS_LIGHT = 8
}

interface IVelocityMethodVMProps {
    vm?: DelineateWatershedInputStateVM;
    history?: any;
}

type VelocitySelectType = FlowTypeIntegers | SheetOptionIntegers | ShallowOptionIntegers | '';
type AllowedEmptyNumber = number | '';

// Table Variables/Columns subject to change
interface IVelocityTableData {
    flowType: VelocitySelectType;
    length: AllowedEmptyNumber;
    slope: AllowedEmptyNumber;
    surfaceMannings: VelocitySelectType;
    n: AllowedEmptyNumber;
    velocity: AllowedEmptyNumber;
    time: AllowedEmptyNumber
    area: AllowedEmptyNumber;
    perimeter: AllowedEmptyNumber;
    // Not displayed in UI, used in map graphics
    x?: number;
    y?: number;
}
interface IVelocityTableValidation {
    flowType: boolean;
    length: boolean;
    slope: boolean;
    surfaceMannings: boolean;
    n: boolean;
    velocity: boolean;
    time: boolean
    area: boolean;
    perimeter: boolean;
}
interface IChannelDetails {
    area: AllowedEmptyNumber;
    perimeter: AllowedEmptyNumber;
}
interface IMiscValidations {
    totalTime: boolean;
    channelArea: boolean;
    channelPerimeter: boolean;
}

export const placeholderTableData: IVelocityTableData[] = [
    {
        flowType: FlowTypeIntegers.CHANNEL,
        length: 200,
        slope: 2,
        surfaceMannings: '',
        n: 4,
        velocity: 9,
        time: 13,
        area: 2,
        perimeter: 4,
        x: 100,
        y: 200
    },
    {
        flowType: FlowTypeIntegers.SHALLOW,
        length: 200,
        slope: 2,
        surfaceMannings: ShallowOptionIntegers.PAVED,
        n: 4,
        velocity: 9,
        time: 13,
        area: 2,
        perimeter: 4,
        x: 101,
        y: 201
    },
    {
        flowType: FlowTypeIntegers.SHEET,
        length: 200,
        slope: 2,
        surfaceMannings: SheetOptionIntegers.CULTIVATED_LOW,
        n: 4,
        velocity: 9,
        time: 13,
        area: 2,
        perimeter: 4,
        x: 102,
        y: 202
    },
];
export const defaultEmptyTable: IVelocityTableData[] = [
    {
        flowType: '',
        length: '',
        slope: '',
        surfaceMannings: '',
        n: '',
        velocity: '',
        time: '',
        area: '',
        perimeter: '',
    }
];
export const defaultEmptyValidation: IVelocityTableValidation[] = [
    {
        flowType: false,
        length: false,
        slope: false,
        surfaceMannings: false,
        n: false,
        velocity: false,
        time: false,
        area: false,
        perimeter: false,
    }
]

const InputLabels = {
    flowType: 'Flow Type',
    length: 'Length',
    slope: 'Slope',
    surfaceMannings: 'Surface Manning\'s n',
    n: 'n',
    velocity: 'Velocity',
    time: 'Time',
    area: 'Cross-Section Area',
    perimeter: 'Wetted Perimeter',
    inputHeaderRainfall: "2-Year Rainfall"
}

const separatorSheetPointSymbol = {
    type: 'simple-marker',
    style: 'circle',
    color: 'yellow',
    size: '8px',
    outline: {
        color: 'yellow',
        width: 1
    }
} as any;

const separatorShallowPointSymbol = {
    type: 'simple-marker',
    style: 'circle',
    color: 'orange',
    size: '8px',
    outline: {
        color: 'orange',
        width: 1
    }
} as any;

const separatorChannelPointSymbol = {
    type: 'simple-marker',
    style: 'circle',
    color: 'red',
    size: '8px',
    outline: {
        color: 'red',
        width: 1
    }
} as any;

const flowpathSegmentsSymbol = {
    type: 'simple-line',
    color: 'blue',
    width: 1,
} as any;

const highlightSeperatorSymbol = {
    type: "simple-marker",
    style: "circle",
    color: "transparent",
    size: "20px",
    outline: {
        color: "white",
        width: 1
    }
} as any;

const highlightSegmentSymbol = {
    type: "simple-line",
    color: "lightblue",
    width: 4
} as any;

export class VelocityMethodVM {
    @observable props: IVelocityMethodVMProps;

    constructor(props: IVelocityMethodVMProps) {
        this.props = props;
        if (this.props.vm) {
            this.props.vm.velocityVm = this;
        }

        this.mapView = this.props.vm?.mapView;
        this.separatorGraphicsLayer = this.props.vm?.separatorGraphicsLayer as GraphicsLayer;
        this.flowPathGraphicsLayer = this.props.vm?.flowPathGraphicsLayer as GraphicsLayer;
        this.flowpathSegmentsGraphicsLayer = this.props.vm?.flowpathSegmentsGraphicsLayer as GraphicsLayer;
        this.workingSpatialReference = this.props.vm?.workingSpatialReference as SpatialReference;
        this.separatorSketch = new Sketch({
            id: 'separatorSketch',
            layer: this.separatorGraphicsLayer,
            view: this.mapView,
            creationMode: 'continuous',
            availableCreateTools: ['point'],
            visibleElements: {
                createTools: false,
                selectionTools: false,
                settingsMenu: false
            },
            snappingOptions: {
                enabled: true,
                featureSources: [
                    {
                        layer: this.flowPathGraphicsLayer,
                        enabled: true,
                    }
                ],
                distance: 21
            }
        });
        this.separatorSketch.viewModel.pointSymbol = separatorSheetPointSymbol;
        this.watchHandle1 = this.separatorSketch.on('create', (event) => this.watchHandleCreateCallback(event));
        this.watchHandle2 = this.separatorSketch.on('update', (event) => this.watchHandleUpdateCallback(event));
        this.watchHandle3 = this.separatorSketch.on('delete', () => {
            this.isSegmentsModified = true;
        })
        this.mapContainer = this.mapView?.container as HTMLElement;
    }

    get getFlowPath() {
        return this.props.vm?.flowPathLength;
    }

    public readonly mapView: MapView | undefined;
    public readonly separatorSketch: Sketch;
    public readonly separatorGraphicsLayer: GraphicsLayer;
    public readonly flowPathGraphicsLayer: GraphicsLayer;
    private flowpathSegmentsGraphicsLayer: GraphicsLayer;
    private originalUpdatePoint: Point | undefined;
    private mapContainer: HTMLElement;
    private workingSpatialReference?: SpatialReference = undefined;
    private watchHandle1;
    private watchHandle2;
    private watchHandle3;

    private cumulativeFlowpathLengths: number[] = [];
    private totalFlowpathLength: number = 0;
    private flowpathPolylines: Polyline[] = [];
    private origLength: number = 0;
    private watchHandle4;
    private highlightHandle1;
    private highlightHandle2;
    @observable highlightEnabled = false;
    @observable highlightedRowIndex: number | null = null;
    @observable highlightedGraphicIndex: number | null = null;
    @observable originalSymbols: Record<number, __esri.Symbol> = {};

    dispose(): void {
        this.watchHandle1.remove();
        this.watchHandle2.remove();
        this.watchHandle3.remove();
        this.watchHandle4.remove();
        this.highlightHandle1.remove();
        this.highlightHandle2.remove();
        this.separatorSketch.destroy();
    }

    @observable isSegmentsModified: boolean = false;
    @observable disableReset: boolean = false;
    @observable inputsChanged: number = 0;

    @observable drawFlowSegmentToggle: boolean = false;
    @observable activeSegmentButton = FlowTypeIntegers.SHEET;// Button set has 1 option mandatory
    @observable selectedMethod: number = SelectedMethod.LAG;
    @observable viewedChannelIndex: number = 0;
    @observable showDetailsContainer: boolean = false;
    @observable temporaryChannelDetails: IChannelDetails = {
        area: 0,
        perimeter: 0
    };

    @observable tableDataMode: number = TableMode.MANUAL;
    readonly channelDetailsButtons = [
        { title: 'Close', disabled: false, onClick: this.closeDetailsContainer, className: 'usa-button--outline' },
        { title: 'Save', disabled: false, onClick: this.saveChannelDetails, className: 'usa-button' },
    ];

    @observable tableData: IVelocityTableData[] = [...defaultEmptyTable];
    @observable tableValidationData: IVelocityTableValidation[] = [...defaultEmptyValidation];

    @observable totalTime: AllowedEmptyNumber = '';
    @observable initialTotalTime: AllowedEmptyNumber = '';
    @observable totalLengthResult: AllowedEmptyNumber = '';
    @observable averageVelocity: AllowedEmptyNumber = '';
    @observable inputHeaderRainfall: AllowedEmptyNumber = '';
    @observable designElementId: number = 0;
    @observable miscValidations: IMiscValidations = {
        totalTime: false,
        channelArea: false,
        channelPerimeter: false
    }
    @observable formHasError: boolean = false;
    readonly tableInputOptions = [
        {
            name: 'Sheet',
            value: FlowTypeIntegers.SHEET
        },
        {
            name: 'Shallow Concentrated',
            value: FlowTypeIntegers.SHALLOW
        },
        {
            name: 'Channel',
            value: FlowTypeIntegers.CHANNEL
        }
    ];
    readonly shallowOptions = [
        {
            name: 'Paved',
            value: ShallowOptionIntegers.PAVED
        },
        {
            name: 'Unpaved',
            value: ShallowOptionIntegers.UNPAVED
        }
    ];
    readonly sheetOptions = [
        {
            name: 'Cultivated - 20% Residue (0.06)',
            value: SheetOptionIntegers.CULTIVATED_LOW
        },
        {
            name: 'Cultivated - 20% Residue (0.17)',
            value: SheetOptionIntegers.CULTIVATED_HIGH
        },
        {
            name: 'Grass - Bermuda (0.41)',
            value: SheetOptionIntegers.GRASS_BERMUDA
        },
        {
            name: 'Grass - Dense (0.24)',
            value: SheetOptionIntegers.GRASS_DENSE
        },
        {
            name: 'Grass - Range, Short (0.15)',
            value: SheetOptionIntegers.GRASS_RANGE
        },
        {
            name: 'Fallow - No Residue (0.05)',
            value: SheetOptionIntegers.FALLOW
        },
        {
            name: 'Range - Natural (0.13)',
            value: SheetOptionIntegers.RANGE
        },
        {
            name: 'Woods - Dense (0.80)',
            value: SheetOptionIntegers.WOODS_DENSE
        },
        {
            name: 'Woods - Light (0.40)',
            value: SheetOptionIntegers.WOODS_LIGHT
        },
    ]

    @action.bound async init() {
        this.props.vm!.jobExecuting = true;
        this.props.vm!.jobMessages = ['Loading Rainfall Data...'];
        this.inputsChanged = 0;
        try {
            const rainfall = await this.get2YrRainfallData();
            this.inputHeaderRainfall = rainfall;
            this.props.vm!.jobMessages = ['Loading Existing Flow Segments...'];
            await this.getSeparatorsData();
            this.props.vm!.jobMessages = ['Exporting GDB to GeoTiff...'];
            const geoTiffResponse = await this.callGpServices();
            if (geoTiffResponse && geoTiffResponse.result_geotiff_file && geoTiffResponse.result_geotiff_file.length > 0) {
                this.props.vm!.jobMessages = ['Saving DEM...'];
                const saveResponse = await this.saveOnlineDem(geoTiffResponse?.result_geotiff_file);
            }
            else if (!geoTiffResponse) {
                // something went wrong in geotiff
            }
            else {
                // Assume we already saved
            }
            await this.validateAndFlipFlowpathIfNeeded();
            this.watchHandle4 = this.mapView!.on('pointer-move', this.handlePointerMove);
        }
        catch (error) {
            // Handle error in here
            console.error(error);
        }
        this.props.vm!.jobExecuting = false;
        this.props.vm!.jobMessages = [''];
    }

    async get2YrRainfallData(): Promise<number | ''> {
        try {
            if (!efh2Store.gisModel.gpSessionData.output_WS_WATERSHED || !efh2Store.gisModel.gpSessionData.output_WS_WATERSHED.features) {
                return '';
            }
            const polygon = efh2Store.gisModel.gpSessionData.output_WS_WATERSHED.features[0].geometry as Polygon;
            const centroid = polygon.centroid;
            const rainfallCellID = await getRainfallCellID(centroid);
            if (rainfallCellID && efh2Store.projectsStore.currentProject.state !== "WA") {
                const rainfall = await HydroControllerApi.get2YearRainfallDistributionByRainfallCellIdUsingGET(rainfallCellID);
                if (rainfall && rainfall.data && rainfall.data.year2Rainfall) {
                    return rainfall.data.year2Rainfall;
                }
            } else {
                MessageCenter.addWarningFromCode('Hydro_75')
            }
        }
        catch (error) {
            console.error(error);
        }
        return '';
    }
    private parseNumericValues(value: number | undefined, decimals?: number): AllowedEmptyNumber {
        if (value !== undefined) {
            if (decimals) {
                return this.formatClean(value, decimals);
            }
            else {
                return value;
            }
        }
        else return '';
    }
    private parseSurfaceMannings(flowType: number, sm: number | undefined): VelocitySelectType | '' {
        if (sm === undefined) {
            return '';
        }
        switch (flowType) {
            case FlowTypeIntegers.CHANNEL:
                return '';
            case FlowTypeIntegers.SHEET:
                if (Object.values(SheetOptionIntegers).includes(sm)) {
                    return sm;
                }
                else {
                    return '';
                }
            case FlowTypeIntegers.SHALLOW:
                if (Object.values(ShallowOptionIntegers).includes(sm)) {
                    return sm;
                }
                else {
                    return '';
                }
            default:
                return '';
        }
    }
    @action.bound loadExistingSegments(existingData: LoadFlowPathSegmentsRespDTO) {
        try {
            // Parse data and set in tableData
            const responseSegments = existingData.flowPathSegments ?? [];
            // results column
            this.totalLengthResult = this.parseNumericValues(existingData.totalLength, 1);
            this.totalTime = this.parseNumericValues(existingData.timeOfConcentration, 2);
            this.averageVelocity = this.parseNumericValues(existingData.averageVelocity, 2)
            this.initialTotalTime = this.parseNumericValues(existingData.timeOfConcentration, 2)
            // Overwrites rainfall if this remains a changable input field
            const rainfall = this.parseNumericValues(existingData.rainfallDistribution, 2);
            if (rainfall !== '') {
                this.inputHeaderRainfall = rainfall;
            }
            if (responseSegments && responseSegments.length > 0) {
                this.tableData = [];
                this.tableValidationData = [];
                responseSegments.forEach((segment, index) => {
                    const parsedRow = { ...defaultEmptyTable[0] };
                    // general
                    parsedRow.flowType = this.parseNumericValues(segment.flowTypeId);
                    parsedRow.length = this.parseNumericValues(segment.segmentLength, 1);
                    parsedRow.slope = this.parseNumericValues(segment.segmentSlope, 2);
                    parsedRow.time = this.parseNumericValues(segment.subTimeOfConcentration, 2);
                    parsedRow.velocity = this.parseNumericValues(segment.velocity, 2);
                    parsedRow.x = segment.separatorX ? segment.separatorX : undefined;// if undefined or 0, use undefined, else use the assumed number
                    parsedRow.y = segment.separatorY ? segment.separatorY : undefined;


                    if (parsedRow.flowType === FlowTypeIntegers.SHEET || parsedRow.flowType === FlowTypeIntegers.SHALLOW) {
                        // sheet or shallow
                        parsedRow.surfaceMannings = this.parseSurfaceMannings(parsedRow.flowType, segment.surfaceManning);
                    }
                    else {
                        //channel
                        parsedRow.n = this.parseNumericValues(segment.manningn, 4);
                        parsedRow.area = this.parseNumericValues(segment.crossSectionalFlowArea, 2);
                        parsedRow.perimeter = this.parseNumericValues(segment.wettedPerimeter, 2);
                    }
                    this.tableData.push(parsedRow);
                    this.tableValidationData.push({ ...defaultEmptyValidation[0] });
                })
                if (this.tableDataMode === TableMode.MANUAL) {
                    this.tableData.push({ ...defaultEmptyTable[0] })
                    this.tableValidationData.push({ ...defaultEmptyValidation[0] });
                }
            }
        }
        catch (error) {
            console.error(error);
        }
    }
    @action.bound async callGpServices() {
        try {
            return await this.props.vm?.callConvertGDB();
        }
        catch {
            return undefined;
        }
    }
    @action.bound async saveOnlineDem(url?: string) {
        try {
            const projectUuid = efh2Store.projectsStore.currentProject.projectUuid;
            const units = this.props.vm?.in_dem_elevation_units ? this.props.vm?.in_dem_elevation_units : 'meters';
            const gpUrl = appConfig.hydrologyRefactorGpApiConfig.baseUrl.replace('/rest/services/nets/', '');
            const combinedUrl = `${url},${gpUrl}`;
            const response = await DesignElementController.saveOnlineDEMByProjectUuidWithIntervalUsingPOST(projectUuid, units, units, -1, combinedUrl);
            if (response && response.data && response.data.status?.toLowerCase() === 'success') {
                return response;
            }
            else {
                return undefined;
            }
        }
        catch (e) {
            console.error(e)
        }
    }

    @action.bound initSeparatorSketch() {
        this.props.vm?.aoiSketch.cancel();
        this.props.vm?.outletSketch.cancel();
        this.props.vm?.culvertSketch.cancel();
        this.props.vm?.watershedSketch.cancel();
        this.props.vm?.flowPathSketch.cancel();
        this.separatorSketch.create('point');
        document.addEventListener('mousedown', this.handleOutsideClick);
    }

    @action.bound flipFlowSegmentToggle() {
        this.drawFlowSegmentToggle = !this.drawFlowSegmentToggle;
        if (this.drawFlowSegmentToggle) {
            this.deactivate();
        } else {
            this.initSeparatorSketch();
        }
    }
    @action.bound async callBothApis() {
        try {
            await EftProjectController.updateUserInputNotesByProjectUuidUsingPOST({
                projectUuid: efh2Store.projectsStore.currentProject.projectUuid,
                userInputNotes: `TimeOfConcentrationMethod: ${this.selectedMethod === 0 ? "Lag" : "Velocity"}`,
            })
            if (this.selectedMethod === SelectedMethod.VELOCITY) {
                const flowPathSegments = this.tableData
                    .filter(row => row.flowType)
                    .map(row => {
                        const isChannel = row.flowType === 3;
                        return {
                            flowTypeId: row.flowType,
                            separatorX: row.x ?? 0,
                            separatorY: row.y ?? 0,
                            segmentLength: Number(row.length) || 0,
                            segmentSlope: Number(row.slope) || 0,
                            surfaceManning: !isChannel ? Number(row.surfaceMannings) || 0 : 0,
                            manningn: isChannel ? Number(row.n) || 0 : 0,
                            crossSectionalFlowArea: isChannel ? Number(row.area) || 0 : 0,
                            wettedPerimeter: isChannel ? Number(row.perimeter) || 0 : 0,
                            velocity: Number(row.velocity),
                            subTimeOfConcentration: Number(row.time),
                            notes: ""
                        };
                    });
                const reqPayload = {
                    contourInterval: this.props.vm?.in_contour_interval,
                    designElementId: this.designElementId,
                    designElementName: "Velocity",
                    designElementTypeId: 26,
                    json: JSON.stringify({
                        sessionUuid: efh2Store.projectsStore.currentProject.sessionList[0].sessionUuid,
                        rainfallDistribution: this.inputHeaderRainfall,
                        flowPathSegments: flowPathSegments,
                        totalLength: this.totalLengthResult,
                        timeOfConcentration: this.totalTime,
                        averageVelocity: this.averageVelocity
                    }),
                    sessionId: efh2Store.projectsStore.currentProject.sessionList[0].sessionUuid,
                    surfaceDefId: 9166
                }
                await DesignElementController.createOrUpdateTimeOfConcentrationUsingPOST(reqPayload)
            }
        } catch (error) {
            console.error(error)
        }
    }
    @action.bound async resetAction() {
        this.totalTime = '';
        this.averageVelocity = '';
        this.activeSegmentButton = 1;
        this.selectSegmentButton({ target: { value: FlowTypeIntegers.SHEET } })
        this.totalLengthResult = '';
        this.tableData = [...defaultEmptyTable];
        this.tableDataMode = TableMode.MANUAL;
        this.tableValidationData = [...defaultEmptyValidation];
        this.miscValidations = {
            totalTime: false,
            channelArea: false,
            channelPerimeter: false
        }
        this.clearSeparatorGraphics();
        this.highlightEnabled = false;
        MessageCenter.resetMessageCenter();
        efh2Store.velocityInputUpdated = true
    }
    @action.bound selectSegmentButton(e) {
        const { value } = e.target;
        this.activeSegmentButton = +value;
        if (+value === FlowTypeIntegers.SHEET) {
            this.separatorSketch.viewModel.pointSymbol = separatorSheetPointSymbol;
        } else if (+value === FlowTypeIntegers.SHALLOW) {
            this.separatorSketch.viewModel.pointSymbol = separatorShallowPointSymbol;
        } else if (+value === FlowTypeIntegers.CHANNEL) {
            this.separatorSketch.viewModel.pointSymbol = separatorChannelPointSymbol;
        }
    }

    @action.bound async selectMethod(e) {
        const { name } = e.target;
        switch (name) {
            case 'method-velocity':
                if (this.props.vm?.isEditWatershedOn) this.props.vm!.editWatershedSwitchHandler();
                if (this.props.vm?.isEditFlowPathOn) this.props.vm!.editFlowPathSwitchHandler();
                this.selectedMethod = SelectedMethod.VELOCITY;
                await this.init();
                this.initSeparatorSketch();
                break;
            case 'method-lag':
            default:
                efh2Store.velocityInputUpdated = false;
                this.selectedMethod = SelectedMethod.LAG;
                this.deactivate();
                this.clearSeparatorGraphics();
                this.removeMapWatchHandler();
                if (!this.props.vm?.isEditWatershedOn) this.props.vm!.editWatershedSwitchHandler();
                if (!this.props.vm?.isEditFlowPathOn) this.props.vm!.editFlowPathSwitchHandler();
                break;
        }
    }
    get lagSelected(): boolean {
        return this.selectedMethod === SelectedMethod.LAG;
    }
    get velocitySelected(): boolean {
        return this.selectedMethod === SelectedMethod.VELOCITY;
    }

    @action.bound async getSeparatorsData() {
        try {
            if (efh2Store.projectsStore.currentSessionId) {
                // const response = await HydroWatershedController.loadFlowPathSegmentsUsingGET("180DDC11-2538-48F2-975C-4D9ACBF1842B");
                const response = await HydroWatershedController.loadFlowPathSegmentsUsingGET(efh2Store.projectsStore.currentSessionId);
                const result = response.data;

                if (result.flowPathSegments && result.flowPathSegments.length > 0) {
                    this.checkSegmentXY(result.flowPathSegments);
                    await this.loadSeparatorGraphics(result.flowPathSegments);// Set map from segment data
                    await this.loadExistingSegments(result);// Set table from segment data
                    this.generateFlowpathSegmentsBySeparators();
                    this.drawFlowSegmentToggle = false;
                }
            }
        } catch (e) {
            console.error(e);
        }
    }
    @action.bound checkSegmentXY(segments: FlowPathSegment[]) {
        let xyNonZero: boolean = false;
        for (const segment of segments) {
            if (segment.separatorX !== 0 || segment.separatorY !== 0) {
                xyNonZero = true;
                break;
            }
        }
        if (xyNonZero === true) {
            this.tableDataMode = TableMode.SEGMENT;
        }
        else {
            this.tableDataMode = TableMode.MANUAL;
        }
    }

    @action.bound async loadSeparatorGraphics(flowPathSegments: any) {
        this.clearSeparatorGraphics();
        if (!esriProjection.isLoaded()) await esriProjection.load();

        for (let i = 0; i < flowPathSegments.length - 1; i++) {
            const item = flowPathSegments[i];

            const pt = new Point({
                x: item.separatorX,
                y: item.separatorY,
                // spatialReference: { wkid: 32164 }
                spatialReference: this.workingSpatialReference
            });

            const projected = esriProjection.project(pt, this.mapView!.spatialReference) as Point;

            let separatorType = FlowTypeIntegers.SHEET;
            let symbol = null;

            switch (item.flowTypeId) {
                case 1:
                    separatorType = FlowTypeIntegers.SHEET;
                    symbol = separatorSheetPointSymbol;
                    break;
                case 2:
                    separatorType = FlowTypeIntegers.SHALLOW;
                    symbol = separatorShallowPointSymbol;
                    break;
                case 3:
                    separatorType = FlowTypeIntegers.CHANNEL;
                    symbol = separatorChannelPointSymbol;
                    break;
                default:
                    break;
            }

            const graphic = new Graphic({
                geometry: projected,
                attributes: {
                    separatorIndex: i,
                    separatorType,
                    ...item
                },
                symbol: symbol!
            });

            this.separatorGraphicsLayer.graphics.add(graphic);
        };
        this.saveAllOriginalSymbols();
    }

    @action.bound async clearSeparatorGraphics() {
        this.separatorGraphicsLayer.graphics.removeAll();
        this.flowpathSegmentsGraphicsLayer.graphics.removeAll();
    }

    @action.bound async splitSegmentCallback() {
        if (!(this.workingSpatialReference &&
            this.mapView!.spatialReference &&
            this.flowPathGraphicsLayer.graphics.length > 0 &&
            this.separatorGraphicsLayer.graphics.length > 0
        )) return;

        this.deactivate();
        efh2Store.velocityInputUpdated = true;
        if (!esriProjection.isLoaded()) await esriProjection.load();

        if (!this.totalFlowpathLength) {
            await this.calculateFlowpathLength();
        }

        const flowPathPoints: any[] = [];
        const flowPathGraphics = this.flowPathGraphicsLayer.graphics;
        const geom = flowPathGraphics.getItemAt(0).geometry;
        if (geom?.type === 'polyline') {
            const polyline = geom as Polyline;
            const projectedFlowpath = esriProjection.project(polyline, this.workingSpatialReference!) as Polyline;
            projectedFlowpath.paths[0].forEach((coords, i) => {
                flowPathPoints.push({
                    id: i,
                    pointName: null,
                    x: coords[0],
                    y: coords[1],
                    z: 0,
                    visiable: true,
                    tinnable: true,
                    description: null
                });
            });
        }

        const sortedSeparators = this.sortSeparatorGraphics();
        if (!sortedSeparators) return;

        const separatorPoints: any[] = sortedSeparators.map((item, i) => {
            const projectedPt = esriProjection.project(item.geometry, this.workingSpatialReference!) as Point;
            return {
                id: i,
                pointName: item.attributes?.separatorType || null,
                x: projectedPt.x,
                y: projectedPt.y,
                z: 0,
                visiable: true,
                tinnable: true,
                description: null
            };
        });

        const payload = {
            sessionUuid: efh2Store.projectsStore.currentSessionId,
            flowPath: flowPathPoints,
            separators: separatorPoints
        };

        try {
            const response = (await HydroWatershedController.splitSegmentBySeparatorsUsingPOST(payload)).data;

            if (response.segmentLength && response.slopes && response.separators) {
                this.tableData = [];
                this.tableValidationData = [];
                let totalLength: number = 0;
                const newTableData: IVelocityTableData[] = response.segmentLength.map((len: number, i: number) => {
                    const typeStr = response.separators![i].pointName;
                    const typeInt = parseInt(typeStr ?? '');
                    const isValidFlowType = typeInt === FlowTypeIntegers.SHEET || typeInt === FlowTypeIntegers.SHALLOW || typeInt === FlowTypeIntegers.CHANNEL;
                    totalLength += len;

                    return {
                        flowType: isValidFlowType ? typeInt as FlowTypeIntegers : FlowTypeIntegers.CHANNEL,
                        length: this.formatClean(len, 1),
                        slope: this.formatClean(response.slopes![i], 2),
                        x: response.separators![i].x,
                        y: response.separators![i].y,
                        surfaceMannings: '',
                        n: '',
                        velocity: '',
                        time: '',
                        area: '',
                        perimeter: ''
                    };
                });

                const newValidationData: IVelocityTableValidation[] = newTableData.map(() => ({
                    flowType: false,
                    length: false,
                    slope: false,
                    surfaceMannings: false,
                    n: false,
                    velocity: false,
                    time: false,
                    area: false,
                    perimeter: false
                }));

                this.tableValidationData = newValidationData;
                this.tableData = newTableData;
                this.isSegmentsModified = false;
                this.tableDataMode = TableMode.SEGMENT;
                if (this.props.vm) {
                    this.props.vm.flowPathLength = totalLength.toFixed(2);
                }
            } else {
                this.tableData = [...defaultEmptyTable];
                MessageCenter.addNotificationFromCode_object({ code: 'Hydro_109' });
            }
        } catch (e) {
            console.error(e);
            MessageCenter.addNotificationFromCode_object({ code: 'Hydro_109' });
        }
        this.generateFlowpathSegmentsBySeparators();
        this.saveAllOriginalSymbols();
    }

    @action.bound sortSeparatorGraphics() {
        const separatorGraphics = this.separatorGraphicsLayer.graphics;
        if (separatorGraphics.length <= 0) return;

        const sorted = [...separatorGraphics].map(graphic => {
            const separatorPoint = graphic.geometry as Point;
            const closestIdx = this.getFlowpathPathIndexBySeparator(separatorPoint);
            const segPath = this.flowpathPolylines[closestIdx].paths[0];
            const polyline = new Polyline({
                paths: [[[separatorPoint.x, separatorPoint.y], [segPath[0][0], segPath[0][1]]]],
                spatialReference: this.mapView!.spatialReference
            });
            const distToStartOfSegment = esriGeometryEngine.geodesicLength(polyline, 'feet');
            const ratio = (this.cumulativeFlowpathLengths[closestIdx] + distToStartOfSegment) / this.totalFlowpathLength;
            return { graphic, ratio };
        }).sort((a, b) => a.ratio - b.ratio);

        this.separatorGraphicsLayer.graphics.removeAll();
        sorted.forEach((item, i) => {
            item.graphic.attributes.separatorIndex = i;
            this.separatorGraphicsLayer.graphics.add(item.graphic);
        });

        return sorted.map(item => item.graphic);
    }

    @action.bound async updateSeparatorsLocationByLength(origLength: number, newLength: number, index: number) {
        if (!(this.mapView!.spatialReference &&
            this.flowPathGraphicsLayer.graphics.length > 0 &&
            this.separatorGraphicsLayer.graphics.length > 0
        )) return;

        const deltaFeet = newLength - origLength;
        const movingForward = deltaFeet > 0;

        const separatorGraphic = this.separatorGraphicsLayer.graphics.find((g) =>
            g.attributes?.separatorIndex === index
        );
        let currentPoint = separatorGraphic.geometry as Point;
        const closestIdx = this.getFlowpathPathIndexBySeparator(currentPoint);

        let remaining = Math.abs(deltaFeet);
        let found = false;
        let from = currentPoint;

        for (let j = closestIdx; j < this.flowpathPolylines.length && j >= 0; movingForward ? j++ : j--) {
            const currentPath = this.flowpathPolylines[j].paths[0];
            const to = new Point({
                x: movingForward ? currentPath[1][0] : currentPath[0][0],
                y: movingForward ? currentPath[1][1] : currentPath[0][1],
                spatialReference: this.mapView!.spatialReference
            });
            const polyline = new Polyline({
                paths: [[[from.x, from.y], [to.x, to.y]]],
                spatialReference: this.mapView!.spatialReference
            });
            const segLen = esriGeometryEngine.geodesicLength(polyline, "feet");

            if (remaining <= segLen) {
                const densified = esriGeometryEngine.geodesicDensify(polyline, 1, "feet") as Polyline;
                const densifiedPath = densified.paths[0];

                let traveled = 0;
                for (let i = 1; i < densifiedPath.length; i++) {
                    const pt1 = new Point({
                        x: densifiedPath[i - 1][0],
                        y: densifiedPath[i - 1][1],
                        spatialReference: this.mapView!.spatialReference
                    });
                    const pt2 = new Point({
                        x: densifiedPath[i][0],
                        y: densifiedPath[i][1],
                        spatialReference: this.mapView!.spatialReference
                    });
                    const seg = new Polyline({
                        paths: [[[pt1.x, pt1.y], [pt2.x, pt2.y]]],
                        spatialReference: this.mapView!.spatialReference
                    });
                    const segLenFeet = esriGeometryEngine.geodesicLength(seg, "feet");

                    if (traveled + segLenFeet >= remaining) {
                        const leftover = remaining - traveled;
                        const ratio = leftover / segLenFeet;
                        const interpX = pt1.x + (pt2.x - pt1.x) * ratio;
                        const interpY = pt1.y + (pt2.y - pt1.y) * ratio;

                        const movedPoint = new Point({
                            x: interpX,
                            y: interpY,
                            spatialReference: this.mapView!.spatialReference
                        });
                        currentPoint = this.snapPointToFlowpath(movedPoint);
                        found = true;
                        break;
                    } else {
                        traveled += segLenFeet;
                    }
                }
            } else {
                remaining -= segLen;
                from = to;
            }
        }

        if (found && separatorGraphic) {
            separatorGraphic.geometry = currentPoint;
        }

        this.generateFlowpathSegmentsBySeparators();
    }

    @action.bound snapPointToFlowpath(point: Point): Point {
        const flowpathGraphic = this.flowPathGraphicsLayer.graphics.getItemAt(0);
        const flowpathPolyline = flowpathGraphic.geometry as Polyline;

        const snappedPoint = esriGeometryEngine.nearestCoordinate(flowpathPolyline, point)?.coordinate;

        if (!snappedPoint) {
            console.warn("Snap failed: no closest coordinate found.");
            return point;
        }

        return new Point({
            x: snappedPoint.x,
            y: snappedPoint.y,
            spatialReference: this.mapView!.spatialReference
        });
    }

    @action.bound saveAllOriginalSymbols() {
        this.originalSymbols = {};

        this.separatorGraphicsLayer.graphics.forEach((graphic) => {
            const index = graphic.attributes?.separatorIndex;
            if (typeof index === 'number' && graphic.symbol) {
                this.originalSymbols[index] = graphic.symbol;
            }
        });
    }

    @action.bound async updateSeparatorsSymbolByType(newType: VelocitySelectType, index: number) {
        const graphic = this.separatorGraphicsLayer.graphics.find((g) =>
            g.attributes?.separatorIndex === index
        );
        if (graphic) {
            graphic.attributes.separatorType = newType;
            if (newType === FlowTypeIntegers.SHEET) {
                graphic.symbol = separatorSheetPointSymbol;
                this.originalSymbols[index] = separatorSheetPointSymbol;
            } else if (newType === FlowTypeIntegers.SHALLOW) {
                graphic.symbol = separatorShallowPointSymbol;
                this.originalSymbols[index] = separatorShallowPointSymbol;
            } else if (newType === FlowTypeIntegers.CHANNEL) {
                graphic.symbol = separatorChannelPointSymbol;
                this.originalSymbols[index] = separatorChannelPointSymbol;
            }
        }
    }

    @action.bound async validateAndFlipFlowpathIfNeeded() {
        if (!this.workingSpatialReference || !this.flowPathGraphicsLayer.graphics.length) return;

        const graphic = this.flowPathGraphicsLayer.graphics.getItemAt(0);
        if (!graphic || graphic.geometry.type !== 'polyline') return;

        const polyline = graphic.geometry as Polyline;
        const path = polyline.paths[0];
        if (path.length < 2) return;

        const startXY = new Point({
            x: path[0][0],
            y: path[0][1],
            spatialReference: this.mapView!.spatialReference
        });
        const endXY = new Point({
            x: path[path.length - 1][0],
            y: path[path.length - 1][1],
            spatialReference: this.mapView!.spatialReference
        });

        if (!esriProjection.isLoaded()) await esriProjection.load();

        const startProjected = esriProjection.project(startXY, this.workingSpatialReference!) as Point;
        const endProjected = esriProjection.project(endXY, this.workingSpatialReference!) as Point;

        const sessionUuid = efh2Store.projectsStore.currentSessionId;

        const [startResponse, endResponse] = await Promise.all([
            StructureController.getElevationBySessionUuidForVelocityUsingPOST({
                basePoint: { x: startProjected.x, y: startProjected.y },
                sessionUuid
            }),
            StructureController.getElevationBySessionUuidForVelocityUsingPOST({
                basePoint: { x: endProjected.x, y: endProjected.y },
                sessionUuid
            })
        ]);

        const zStart = startResponse.data?.basePoint?.z;
        const zEnd = endResponse.data?.basePoint?.z;

        if (zStart == null || zEnd == null) return;

        if (zEnd > zStart) {
            const reversedPath = [...path].reverse();
            const newPolyline = new Polyline({
                paths: [reversedPath],
                spatialReference: polyline.spatialReference
            });
            graphic.geometry = newPolyline;
        }
    }

    @action.bound async calculateFlowpathLength() {
        const flowpathGraphics = this.flowPathGraphicsLayer.graphics;
        if (flowpathGraphics.length <= 0) return;

        const flowpathGeometry = flowpathGraphics.getItemAt(0).geometry;
        if (flowpathGeometry?.type !== 'polyline') return;

        const polyline = flowpathGeometry as Polyline;
        const path = polyline.paths[0];
        this.cumulativeFlowpathLengths = [0];
        this.flowpathPolylines = [];

        for (let i = 0; i < path.length - 1; i++) {
            const segStart = new Point({ x: path[i][0], y: path[i][1], spatialReference: this.mapView!.spatialReference });
            const segEnd = new Point({ x: path[i + 1][0], y: path[i + 1][1], spatialReference: this.mapView!.spatialReference });
            const lineSegment = new Polyline({
                paths: [[[segStart.x, segStart.y], [segEnd.x, segEnd.y]]],
                spatialReference: this.mapView!.spatialReference
            });
            this.flowpathPolylines.push(lineSegment);

            const len = esriGeometryEngine.geodesicLength(lineSegment, 'feet') || 0;
            this.cumulativeFlowpathLengths.push(this.cumulativeFlowpathLengths[i] + len);
        }

        this.totalFlowpathLength = this.cumulativeFlowpathLengths[this.cumulativeFlowpathLengths.length - 1];
    }

    @action.bound getFlowpathPathIndexBySeparator(separator: Point) {
        const projectedSeparator = esriProjection.project(separator, this.mapView!.spatialReference) as Point;

        let minDist = Infinity;
        let closestIdx = 0;

        for (let i = 0; i < this.flowpathPolylines.length; i++) {
            const dist = esriGeometryEngine.distance(projectedSeparator, this.flowpathPolylines[i], "feet");
            if (dist < minDist) {
                minDist = dist;
                closestIdx = i;
            }
        }
        return closestIdx;
    }

    @action.bound generateFlowpathSegmentsBySeparators() {
        this.flowpathSegmentsGraphicsLayer.graphics.removeAll();

        this.highlightEnabled = true;

        const flowGraphic = this.flowPathGraphicsLayer.graphics.getItemAt(0);
        const flowPath = flowGraphic.geometry as Polyline;
        const separatorPoints = this.separatorGraphicsLayer.graphics.map(g => g.geometry as Point);
        const spatialRef = this.mapView!.spatialReference;

        let remaining = flowPath;
        let originalStart = new Point({
            x: remaining.paths[0][0][0],
            y: remaining.paths[0][0][1],
            spatialReference: spatialRef
        });
        let retry = 0;

        for (let i = 0; i < separatorPoints.length;) {
            const pt = separatorPoints.getItemAt(i);
            const offset = 0.1;

            const left = [pt.x - offset, pt.y - offset * retry];
            const right = [pt.x + offset, pt.y + offset * retry];

            const cutter = new Polyline({
                paths: [[left, right]],
                spatialReference: spatialRef
            });
            const parts = esriGeometryEngine.cut(remaining, cutter) as Polyline[];
            if (parts && parts.length === 2) {
                const candidates = parts.map(part => {
                    const start = new Point({
                        x: part.paths[0][0][0],
                        y: part.paths[0][0][1],
                        spatialReference: spatialRef
                    });
                    const end = new Point({
                        x: part.paths[0][part.paths[0].length - 1][0],
                        y: part.paths[0][part.paths[0].length - 1][1],
                        spatialReference: spatialRef
                    });
                    return { part, start, end };
                });

                const distances = candidates.flatMap(c => [
                    { part: c.part, point: c.start, isStart: true, dist: esriGeometryEngine.distance(originalStart, c.start) },
                    { part: c.part, point: c.end, isStart: false, dist: esriGeometryEngine.distance(originalStart, c.end) }
                ]);

                const closest = distances.reduce((min, curr) => curr.dist < min.dist ? curr : min);
                const part1 = closest.part;
                const part2 = parts[0] === part1 ? parts[1] : parts[0];

                const graphic = new Graphic({
                    geometry: part1,
                    symbol: flowpathSegmentsSymbol
                });
                this.flowpathSegmentsGraphicsLayer.add(graphic);

                remaining = part2;
                originalStart = new Point({
                    x: closest.isStart ? part1.paths[0][1][0] : part1.paths[0][0][0],
                    y: closest.isStart ? part1.paths[0][1][1] : part1.paths[0][0][1],
                    spatialReference: spatialRef
                });
                i++;
                retry = 0;
            } else {
                if (retry === 10) {
                    this.highlightEnabled = false;
                    break;
                } else {
                    retry++;
                }
            }
        }

        if (remaining) {
            const graphic = new Graphic({ geometry: remaining, symbol: flowpathSegmentsSymbol, });
            this.flowpathSegmentsGraphicsLayer.add(graphic);
        }

        this.flowpathSegmentsGraphicsLayer.graphics.forEach((graphic, i) => {
            graphic.attributes = { segmentIndex: i }
        });
    }

    @action.bound handlePointerMove(event: __esri.ViewPointerMoveEvent) {
        if (!this.highlightEnabled) return;

        this.mapView?.hitTest(event).then((response) => {
            const hit = response.results.find(r =>
                r.graphic.layer === this.separatorGraphicsLayer ||
                r.graphic.layer === this.flowpathSegmentsGraphicsLayer
            );

            const newIndex =
                hit?.graphic?.attributes?.separatorIndex ??
                hit?.graphic?.attributes?.segmentIndex;

            if (newIndex !== undefined && newIndex !== this.highlightedRowIndex) {
                this.highlightedRowIndex = newIndex;
                this.highlightSeparatorAtIndex(newIndex);
            } else if (!hit) {
                this.highlightedRowIndex = null;
                this.clearHighlight();
            }
        });
    }

    public async removeMapWatchHandler(): Promise<void> {
        this.flowpathSegmentsGraphicsLayer.graphics.removeAll();
        this.watchHandle4.remove();
    }

    @action.bound async highlightSeparatorAtIndex(index: number) {
        if (!this.highlightEnabled) return;

        const pointGraphic = this.separatorGraphicsLayer.graphics.find((g) =>
            g.attributes?.separatorIndex === index
        );
        const lineGraphic = this.flowpathSegmentsGraphicsLayer.graphics.getItemAt(index);

        this.clearHighlight();

        if (pointGraphic) {
            pointGraphic.symbol = highlightSeperatorSymbol;
        }

        if (lineGraphic) {
            lineGraphic.symbol = highlightSegmentSymbol;
        }

        this.highlightedGraphicIndex = index;
    }

    @action.bound clearHighlight() {
        if (this.highlightedGraphicIndex === null) return;

        const i = this.highlightedGraphicIndex;

        const pointGraphic = this.separatorGraphicsLayer.graphics.find((g) =>
            g.attributes?.separatorIndex === i
        );
        const lineGraphic = this.flowpathSegmentsGraphicsLayer.graphics.getItemAt(i);

        if (pointGraphic && this.originalSymbols[i]) {
            pointGraphic.symbol = this.originalSymbols[i];
        }

        if (lineGraphic) {
            lineGraphic.symbol = flowpathSegmentsSymbol;
        }

        this.highlightedGraphicIndex = null;
    }

    private watchHandleCreateCallback(event) {
        if (event.state === 'complete' && this.mapView) {
            this.highlightEnabled = false;
            this.clearHighlight();
            const lastGraphic = event.graphic;
            if (lastGraphic?.geometry?.type === 'point') {
                const point = lastGraphic.geometry as Point;

                const flowPathGraphic = this.flowPathGraphicsLayer.graphics.getItemAt(0);
                const flowPath = flowPathGraphic?.geometry as Polyline;

                if (!flowPath || flowPath.type !== 'polyline') {
                    this.separatorGraphicsLayer.remove(lastGraphic);
                    return;
                }

                const distance = esriGeometryEngine.distance(flowPath, point);
                const tolerance = 10;

                if (distance > tolerance) {
                    this.separatorGraphicsLayer.remove(event.graphic);
                    return;
                }

                lastGraphic.attributes = {
                    separatorType: this.activeSegmentButton.toString()
                };
                this.isSegmentsModified = true;
            }
        }
    }

    private watchHandleUpdateCallback(event) {
        if (event.state === 'start') {
            this.originalUpdatePoint = event.graphics[0].geometry as Point;
            event.graphics[0].symbol = this.originalSymbols[event.graphics[0].attributes.separatorIndex];
            this.highlightEnabled = false;
            this.clearHighlight();
        }

        if (event.state === 'active' && event.toolEventInfo?.type === 'move-stop') {
            const lastGraphic = event.graphics[0];
            if (lastGraphic?.geometry?.type === 'point') {
                const movedPoint = lastGraphic.geometry as Point;

                const flowPathGraphic = this.flowPathGraphicsLayer.graphics.getItemAt(0);
                const flowPath = flowPathGraphic?.geometry as Polyline;

                if (!flowPath || flowPath.type !== 'polyline') return;

                const nearest = esriGeometryEngine.nearestCoordinate(flowPath, movedPoint);
                const distance = nearest.distance;
                const tolerance = 10;

                if (distance <= tolerance) {
                    const snappedPoint = new Point({
                        latitude: nearest.coordinate.latitude,
                        longitude: nearest.coordinate.longitude,
                        spatialReference: movedPoint.spatialReference
                    });
                    lastGraphic.geometry = snappedPoint;
                    this.isSegmentsModified = true;
                } else {
                    if (this.originalUpdatePoint) {
                        lastGraphic.geometry = this.originalUpdatePoint;
                    }
                }
            }
        }

        if (event.state === 'complete') {
            this.originalUpdatePoint = undefined;
        }
    }

    private handleOutsideClick = async (event: MouseEvent): Promise<void> => {
        const target = event.target as HTMLElement;
        const isSegmentButtonClick = target.closest('.segment-type-group');
        const isFlowSegmentToggleClick = target.closest('.flow-segment-toggle');
        if (isSegmentButtonClick || isFlowSegmentToggleClick) return;
        if (!this.mapContainer.contains(event.target as Node)) {
            this.deactivate();
        }
    };

    public async deactivate(): Promise<void> {
        document.removeEventListener('mousedown', this.handleOutsideClick);
        this.separatorSketch.cancel();
        this.drawFlowSegmentToggle = true;
    }

    @action.bound handleInputChange(event, index: number) {
        const { value, name } = event.target;
        const input = name.replace(`-${index}`, '');
        this.tableData[index][input] = this.checkAndReturnEmpty(value, parseFloat(value));
        this.addNewRow(index);
        if (name && !name.startsWith('time-')) {
            efh2Store.velocityInputUpdated = true;
        } else efh2Store.velocityInputUpdated = false;
    }

    @action.bound handleSelectChange(event, index: number) {
        efh2Store.velocityInputUpdated = true;
        const { value, name } = event.target;
        const input = name.replace(`-${index}`, '');
        const tempValue: VelocitySelectType = value === '' ? value : parseInt(value);
        switch (input) {
            case 'surface-mannings':
                this.tableData[index].surfaceMannings = tempValue;
                break;
            case 'flow-type-dd':
                this.tableData[index].flowType = tempValue;
                if (tempValue === FlowTypeIntegers.SHEET || tempValue === FlowTypeIntegers.SHALLOW) {
                    this.tableValidationData[index].velocity = false;
                    this.tableData[index].velocity = '';
                }
                this.updateSeparatorsSymbolByType(tempValue, index);
                break;
        }
        this.addNewRow(index);
    }
    @action.bound addNewRow(index: number) {
        if (index === this.tableData.length - 1 && this.tableDataMode === TableMode.MANUAL) {
            this.tableData.push({ ...defaultEmptyTable[0] });
            this.tableValidationData.push({ ...defaultEmptyValidation[0] });
        }
    }

    @action.bound handleMiscInputChange(event) {
        const { name, value } = event.target;
        switch (name) {
            case 'totals-time':
                this.totalTime = this.checkAndReturnEmpty(value, parseFloat(value));
                efh2Store.velocityInputUpdated = false;
                break;
        }
    }
    @action.bound async handleMiscBlur(event) {
        this.setResetLock();
        const { name, value } = event.target;
        const valueRounded = formatRound(+value, 2);
        let hasError = false;
        switch (name) {
            case 'totals-time':
                this.totalTime = this.checkAndReturnEmpty(value, valueRounded);
                this.miscValidations.totalTime = await this.validateTime(name, value, valueRounded, 0);
                efh2Store.tocManuallyUpdated = true;
                efh2Store.tocManuallyUpdatedComment = `Velocity Method Time of Concentration was was manually overridden from ${this.initialTotalTime} to ${this.totalTime}`;
                if (this.miscValidations.totalTime) hasError = true;
                break;
            case 'area-details-input':
                this.temporaryChannelDetails.area = this.checkAndReturnEmpty(value, valueRounded);
                this.miscValidations.channelArea = await this.commonValidation(name.replace('-details-input', ''), value, valueRounded);
                if (this.miscValidations.channelArea) hasError = true;
                break;
            case 'perimeter-details-input':
                this.temporaryChannelDetails.perimeter = this.checkAndReturnEmpty(value, valueRounded);
                this.miscValidations.channelPerimeter = await this.commonValidation(name.replace('-details-input', ''), value, valueRounded);
                if (this.miscValidations.channelPerimeter) hasError = true;
                break;
        }
        if (hasError) { this.formHasError = true } else { this.formHasError = false }
        this.unlockReset();
        this.inputsChanged = this.inputsChanged + 1;
    }
    @action.bound async validateForm() {
        MessageCenter.blockCalls([]);
        this.formHasError = false;

        // Validate each row except the last (empty) row
        // Uses tableDataMode being an integer to control where the validation stops if an empty row is expected
        for (let i = 0; i < this.tableData.length - (1 - this.tableDataMode); i++) {
            const row = this.tableData[i];
            // Validate shared inputs
            await this.handleInputBlur({ target: { name: `length-${i}`, value: row.length } }, i);
            await this.handleInputBlur({ target: { name: `slope-${i}`, value: row.slope } }, i);

            // Validate select inputs
            await this.handleSelectBlur({ target: { name: `flow-type-dd-${i}`, value: row.flowType } }, i);

            if (row.flowType === FlowTypeIntegers.SHEET || row.flowType === FlowTypeIntegers.SHALLOW) {
                await this.handleSelectBlur({ target: { name: `surface-mannings-${i}`, value: row.surfaceMannings } }, i);
            } else if (row.flowType === FlowTypeIntegers.CHANNEL) {
                await this.handleInputBlur({ target: { name: `n-${i}`, value: row.n } }, i);
            }

            // If you want to skip validation for velocity/time, set their validation to false here:
            this.tableValidationData[i].velocity = false;
            this.tableValidationData[i].time = false;
        }

        MessageCenter.unblockCalls();
        if (this.formHasError) {
            await MessageCenter.addNotificationFromCode('Common_26');
            return false;
        }
        else {
            const isOrderValid = this.tableDataMode === TableMode.MANUAL ? this.checkFlowTypeOrder(this.tableData) : true;
            if (isOrderValid === false) {
                await MessageCenter.addWarningFromCode('Hydro_109')
                return false;
            }
        }
        return true;
    }
    @computed get totalLength() {
        return this.tableData.reduce((sum, current) => (sum + (+current.length)), 0);
    }

    @computed get getTotalTimeTemp() {
        return formatRound(this.tableData.reduce((sum, current) => (sum + (+current.time)), 0), 3);
    }

    @action.bound closeDetailsContainer() {
        this.showDetailsContainer = false;
    }
    @action.bound openDetailsContainer(index: number) {
        this.viewedChannelIndex = index;
        this.temporaryChannelDetails = {
            area: this.tableData[index].area,
            perimeter: this.tableData[index].perimeter
        };
        this.showDetailsContainer = true;
        setTimeout(() => {
            document.getElementById('area-details-input')?.focus();
        }, 0);
    }
    @action.bound saveChannelDetails() {
        this.tableData[this.viewedChannelIndex].area = this.temporaryChannelDetails.area;
        this.tableData[this.viewedChannelIndex].perimeter = this.temporaryChannelDetails.perimeter;
        this.closeDetailsContainer();
    }
    @action.bound channelDetailsChange(e) {
        const { name, value } = e.target;
        const id = name.replace('-details-input', '');
        this.temporaryChannelDetails[id] = parseFloat(value);
    }

    @action.bound async handleInputFocus(event, index) {
        const { value, name } = event.target;
        const input = name.replace(`-${index}`, '');
        if (input == 'length') {
            this.origLength = value;
        }
    }

    //n=4dec, time=2dec, 2yr rain=input, length=2dec, maybe velocity=4 - 2 for now, totals.time can be input
    @action.bound async handleInputBlur(event, index) {
        this.setResetLock();
        if (this.tableDataMode === TableMode.MANUAL && index === this.tableData.length - 1) {
            // If in the last row without edits, there should be nothing to validate
            return;
        }
        const { value, name } = event.target;
        const input = name.replace(`-${index}`, '');
        let rounded;
        switch (input) {
            case 'n':
                rounded = this.formatClean(value, 4);//removing trailing zeros
                break;
            case 'velocity':
                rounded = this.formatClean(value, 2);
                break;
            case 'slope':
                rounded = this.formatClean(value, 2);
                break;
            case 'length':
                rounded = this.formatClean(value, 1);

                if (this.origLength > 0) {
                    const oldLength = this.origLength;
                    const newLength = parseFloat(value);
                    const delta = newLength - oldLength;

                    if (!isNaN(delta) && delta !== 0) {
                        this.updateSeparatorsLocationByLength(oldLength, newLength, index);

                        // Update adjacent row
                        if (index < this.tableData.length - 1) {
                            const nextLength = parseFloat(this.tableData[index + 1][input]) || 0;
                            this.tableData[index + 1].length = formatRound(nextLength - delta, 1);
                        } else if (index > 0) {
                            const prevLength = parseFloat(this.tableData[index - 1][input]) || 0;
                            this.tableData[index - 1].length = formatRound(prevLength - delta, 1);
                        }
                    }
                }

                this.origLength = 0;
                break;
            default:
                rounded = this.formatClean(value, 2);
                break;
        }
        let hasError = false;
        switch (input) {
            case 'time':
                this.tableData[index][input] = this.checkAndReturnEmpty(value, rounded);
                this.tableValidationData[index].time = await this.validateTime(input, value, rounded, index);
                if (this.tableValidationData[index].time) hasError = true;
                this.totalTime = this.getTotalTimeTemp;
                efh2Store.tocManuallyUpdated = true;
                efh2Store.velocityInputUpdated = false;
                efh2Store.tocManuallyUpdatedComment = `Velocity Method Time of Concentration was manually overridden from ${this.initialTotalTime} to ${this.totalTime}`;
                break;
            case 'n':
                this.tableData[index][input] = this.checkAndReturnEmpty(value, rounded);
                this.tableValidationData[index][input] = await this.validateN(input, value, rounded);
                if (this.tableValidationData[index][input]) hasError = true;
                break;
            default:
                this.tableData[index][input] = this.checkAndReturnEmpty(value, rounded);
                this.tableValidationData[index][input] = await this.validateInput(input, value, rounded);
                if (this.tableValidationData[index][input]) hasError = true;
                break;
        }
        if (hasError) { this.formHasError = true } else { this.formHasError = false };
        this.unlockReset();
        this.inputsChanged = this.inputsChanged + 1;
    }

    @action.bound async handleSelectBlur(event, index) {
        this.setResetLock();
        if (this.tableDataMode === TableMode.MANUAL && index === this.tableData.length - 1) {
            // If in the last row without edits, there should be nothing to validate
            return;
        }
        const { value, name } = event.target;
        const input = name.replace(`-${index}`, '');
        const tempValue: VelocitySelectType = value === '' ? value : parseInt(value);
        let hasError = false;
        switch (input) {
            case 'surface-mannings':
                this.tableValidationData[index].surfaceMannings = await this.dropdownValidation(tempValue);
                if (this.tableValidationData[index].surfaceMannings) hasError = true;
                break;
            case 'flow-type-dd':
                this.tableValidationData[index].flowType = await this.dropdownValidation(tempValue);
                if (this.tableValidationData[index].flowType) hasError = true;
                break;
        }
        if (hasError) { this.formHasError = true } else { this.formHasError = false };
        this.unlockReset();
        this.inputsChanged = this.inputsChanged + 1;
    }

    async validateInput(name, value, rounded) {
        switch (name) {
            case 'length':
            case 'slope':
            case 'n':
            case 'velocity':
            case 'area':
            case 'perimeter':
                return await this.commonValidation(name, value, rounded);
        }
    }
    async validateN(name, value, rounded) {
        const minN = 0.0001;
        const maxN = 1;
        if (value === '' || value === undefined || parseFloat(rounded) < minN || parseFloat(rounded) > maxN) {
            await MessageCenter.addNotificationFromCode_object({ code: 'Formula_02' });
            return true;
        }
        return false;
    }
    async validateTime(name, value, rounded, index) {
        const minimumTime = 0.1;
        const maxTime = 10;
        if (value === '' || value === undefined || parseFloat(value) <= 0) {
            await MessageCenter.addNotificationFromCode_object({ code: 'Common_10', inputName: 'Time' });
            return true;
        }
        else if (rounded > 10) {
            if (name === 'time') {
                this.tableData[index].time = maxTime;
            } else {
                this.totalTime = maxTime;
            }
            await MessageCenter.addNotificationFromCode('Hydro_15');
            return true;
        }
        if (+value < minimumTime && value > 0) {
            await MessageCenter.addWarningFromCode('Hydro_108');
            if (name === 'time') {
                this.tableData[index].time = minimumTime;
            }
            else {
                this.totalTime = minimumTime;
            }
        }
        return false;
    }

    @action.bound checkFlowTypeOrder(flowTypes) {
        if (!flowTypes || flowTypes.length === 0) return false;

        const expectedOrder = [FlowTypeIntegers.SHEET, FlowTypeIntegers.SHALLOW, FlowTypeIntegers.CHANNEL];

        // Filter out invalid entries (like 0 or undefined flowType)
        const cleanedFlowTypes = flowTypes.filter((f) => expectedOrder.includes(Number(f.flowType)));

        if (cleanedFlowTypes.length === 0) return false;

        let hasSheet = false;
        let lastIndex = -1;

        for (let i = 0; i < cleanedFlowTypes.length; i++) {
            const flow = Number(cleanedFlowTypes[i].flowType);
            const currentIndex = expectedOrder.indexOf(flow);

            if (i === 0) {
                if (flow !== FlowTypeIntegers.SHEET) {
                    return false;
                }
                hasSheet = true;
            } else {
                if (!hasSheet) return false;
                if (currentIndex < lastIndex) {
                    return false;
                }
            }

            lastIndex = currentIndex;
        }

        return true;
    }

    @observable async minimumValidation(callLocation?) {
        const isOrderValid = this.tableDataMode === TableMode.MANUAL ? this.checkFlowTypeOrder(this.tableData) : true;
        if (isOrderValid === false) { await MessageCenter.addWarningFromCode('Hydro_109') }
        if ((callLocation && callLocation === 'segmentSplit') || this.tableDataMode === TableMode.SEGMENT) {
            return isOrderValid && (this.inputHeaderRainfall !== '') && (this.tableData.length > 0) && !(this.tableValidationData.some(obj => Object.values(obj).some(Boolean))) && (!this.formHasError) &&
                (this.tableData.every(item => item.flowType && item.length && item.slope && (item.n || item.surfaceMannings) && (item.flowType === FlowTypeIntegers.CHANNEL ? (item.area !== '') && (item.perimeter !== '') : true)))
        }
        else {
            //also checking atleast one row data is complete
            return (this.inputHeaderRainfall !== '') && isOrderValid && (this.tableData.length > 0) && !(this.tableValidationData.some(obj => Object.values(obj).some(Boolean))) && (!this.formHasError) &&
                (this.tableData.slice(0, -1).every(item => item.flowType && item.length && item.slope && (item.n || item.surfaceMannings) && (item.flowType === FlowTypeIntegers.CHANNEL ? (item.area !== '') && (item.perimeter !== '') : true)))
        }
    }

    @computed get suggestedLength() {
        if (this.getFlowPath) {
            const lengthDifference = parseFloat(this.getFlowPath) - this.totalLength;
            if (lengthDifference > 0) {
                return (parseFloat(this.getFlowPath) - this.totalLength).toString();
            }
        }
        return '';
    }
    getErrorClass(value: boolean) {
        return value ? 'usa-input--error' : '';
    }
    private formatClean(value, digits?) {
        const formatted = formatRound(value, digits);
        const num = parseFloat(String(formatted)).toString();
        return num ? num : value;
    }
    async commonValidation(name, value, roundedValue) {
        const validity = value === '' || value === undefined || roundedValue <= 0 ? true : false;
        if (validity) {
            await MessageCenter.addNotificationFromCode_object({ code: 'Common_10', inputName: InputLabels[name] });
        }
        return validity;
    }
    async dropdownValidation(value) {
        if (value === '') {
            await MessageCenter.addNotificationFromCode('Common_26');
            return true;
        }
        else {
            return false;
        }
    }
    checkAndReturnEmpty(value, roundedValue) {
        return value !== '' && value !== undefined ? roundedValue : '';
    }
    private setResetLock() {
        this.disableReset = true;
    }
    private unlockReset() {
        this.disableReset = false;
    }
}
